import { useState, useEffect } from "react";
import { motion } from "motion/react";
import { 
  Mail, MailOpen, Send, Inbox, ChevronLeft, 
  Loader2, Plus, Trash2, Calendar
} from "lucide-react";
import axios from "axios";
import { useAuth, formatApiErrorDetail } from "../../contexts/AuthContext";
import { Section, Button } from "./PageComponents";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function LetterTrackingPage({ connectionId }) {
  const { user } = useAuth();
  const [letters, setLetters] = useState([]);
  const [stats, setStats] = useState(null);
  const [otherProfile, setOtherProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newEvent, setNewEvent] = useState({ event_type: "sent", note: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (connectionId && user?.penpal_profile_id) {
      fetchLetterHistory();
    } else {
      setLoading(false);
    }
  }, [connectionId, user]);

  const fetchLetterHistory = async () => {
    try {
      const { data } = await axios.get(
        `${BACKEND_URL}/api/penpal/letters/${connectionId}`,
        { withCredentials: true }
      );
      setLetters(data.letters);
      setStats(data.stats);
      setOtherProfile(data.other_profile);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAddEvent = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      await axios.post(`${BACKEND_URL}/api/penpal/letters/track`, {
        connection_id: connectionId,
        event_type: newEvent.event_type,
        note: newEvent.note
      }, { withCredentials: true });
      
      setNewEvent({ event_type: "sent", note: "" });
      setShowAddForm(false);
      await fetchLetterHistory();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (letterId) => {
    if (!window.confirm("Delete this entry?")) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/penpal/letters/${letterId}`, { withCredentials: true });
      await fetchLetterHistory();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const formatDate = (isoString) => {
    return new Date(isoString).toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (!user) {
    return (
      <Section>
        <div className="text-center">
          <h2 className="text-3xl font-serif mb-4">Please sign in first</h2>
          <Button href="#login">Sign In</Button>
        </div>
      </Section>
    );
  }

  if (!connectionId) {
    return (
      <Section>
        <div className="text-center">
          <h2 className="text-3xl font-serif mb-4">No connection selected</h2>
          <Button href="#penpal-connections">View Connections</Button>
        </div>
      </Section>
    );
  }

  return (
    <Section id="letter-tracking">
      <div className="max-w-3xl mx-auto" data-testid="letter-tracking-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <a href="#penpal-connections" className="p-2 hover:bg-lws-taupe/10 transition-colors">
              <ChevronLeft size={24} />
            </a>
            <div>
              <h1 className="text-3xl font-serif text-lws-green">Letter Tracking</h1>
              {otherProfile && (
                <p className="text-lws-taupe">Correspondence with {otherProfile.display_name}</p>
              )}
            </div>
          </div>
          
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            className="flex items-center gap-2 px-4 py-2 bg-lws-green text-lws-paper hover:bg-lws-navy transition-colors"
          >
            <Plus size={18} />
            Log Letter
          </button>
        </div>

        {error && (
          <div className="bg-red-50 text-red-600 p-4 mb-6">
            {error}
          </div>
        )}

        {/* Add Form */}
        {showAddForm && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            className="bg-lws-taupe/5 p-6 mb-8"
          >
            <h3 className="text-lg font-serif mb-4">Log a Letter</h3>
            <form onSubmit={handleAddEvent} className="space-y-4">
              <div>
                <label className="block text-xs uppercase tracking-widest text-lws-taupe mb-2">Type</label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="event_type"
                      value="sent"
                      checked={newEvent.event_type === "sent"}
                      onChange={(e) => setNewEvent({ ...newEvent, event_type: e.target.value })}
                      className="accent-lws-green"
                    />
                    <Send size={16} className="text-lws-amber" />
                    <span>I sent a letter</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="event_type"
                      value="received"
                      checked={newEvent.event_type === "received"}
                      onChange={(e) => setNewEvent({ ...newEvent, event_type: e.target.value })}
                      className="accent-lws-green"
                    />
                    <Inbox size={16} className="text-lws-green" />
                    <span>I received a letter</span>
                  </label>
                </div>
              </div>
              
              <div>
                <label className="block text-xs uppercase tracking-widest text-lws-taupe mb-2">Note (optional)</label>
                <input
                  type="text"
                  value={newEvent.note}
                  onChange={(e) => setNewEvent({ ...newEvent, note: e.target.value })}
                  placeholder="e.g., Included pressed flowers, wrote about our trip..."
                  className="w-full border border-lws-ink/10 px-4 py-2 focus:outline-none focus:border-lws-green"
                />
              </div>
              
              <div className="flex gap-4">
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-6 py-2 bg-lws-green text-lws-paper hover:bg-lws-navy transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  {submitting ? <Loader2 size={16} className="animate-spin" /> : null}
                  Save
                </button>
                <button
                  type="button"
                  onClick={() => setShowAddForm(false)}
                  className="px-6 py-2 border border-lws-ink/20 hover:border-lws-green transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </motion.div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 size={32} className="animate-spin text-lws-green" />
          </div>
        ) : (
          <>
            {/* Stats */}
            {stats && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                <div className="bg-lws-amber/10 p-4 text-center">
                  <Send size={24} className="text-lws-amber mx-auto mb-2" />
                  <p className="text-2xl font-serif">{stats.my_letters_sent}</p>
                  <p className="text-xs uppercase tracking-widest text-lws-taupe">Letters Sent</p>
                </div>
                <div className="bg-lws-green/10 p-4 text-center">
                  <Inbox size={24} className="text-lws-green mx-auto mb-2" />
                  <p className="text-2xl font-serif">{stats.my_letters_received}</p>
                  <p className="text-xs uppercase tracking-widest text-lws-taupe">Letters Received</p>
                </div>
                <div className="bg-lws-taupe/10 p-4 text-center">
                  <Mail size={24} className="text-lws-taupe mx-auto mb-2" />
                  <p className="text-2xl font-serif">{stats.total_exchanges}</p>
                  <p className="text-xs uppercase tracking-widest text-lws-taupe">Total Exchanges</p>
                </div>
                <div className="bg-lws-navy/10 p-4 text-center">
                  <MailOpen size={24} className="text-lws-navy mx-auto mb-2" />
                  <p className="text-2xl font-serif">{stats.their_letters_sent}</p>
                  <p className="text-xs uppercase tracking-widest text-lws-taupe">They Sent</p>
                </div>
              </div>
            )}

            {/* Timeline */}
            {letters.length === 0 ? (
              <div className="text-center py-16 bg-lws-taupe/5">
                <Mail size={48} className="text-lws-taupe/30 mx-auto mb-4" />
                <p className="text-xl text-lws-taupe mb-4">No letters tracked yet</p>
                <p className="text-sm text-lws-taupe mb-8">Start logging your correspondence to create a beautiful timeline!</p>
                <button
                  onClick={() => setShowAddForm(true)}
                  className="px-6 py-3 bg-lws-green text-lws-paper hover:bg-lws-navy transition-colors"
                >
                  Log Your First Letter
                </button>
              </div>
            ) : (
              <div className="relative">
                {/* Timeline line */}
                <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-lws-ink/10" />
                
                <div className="space-y-6">
                  {letters.map((letter, idx) => (
                    <motion.div
                      key={letter.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: idx * 0.05 }}
                      className="relative pl-16"
                    >
                      {/* Timeline dot */}
                      <div className={`absolute left-4 w-5 h-5 rounded-full border-2 border-white ${
                        letter.event_type === "sent" ? "bg-lws-amber" : "bg-lws-green"
                      }`} />
                      
                      <div className={`p-4 border ${letter.is_mine ? 'border-lws-ink/10' : 'border-lws-green/20 bg-lws-green/5'}`}>
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-3">
                            {letter.event_type === "sent" ? (
                              <Send size={18} className="text-lws-amber" />
                            ) : (
                              <Inbox size={18} className="text-lws-green" />
                            )}
                            <div>
                              <p className="font-medium">
                                {letter.is_mine ? "You" : letter.profile_name} {letter.event_type === "sent" ? "sent" : "received"} a letter
                              </p>
                              <p className="text-xs text-lws-taupe flex items-center gap-1 mt-1">
                                <Calendar size={12} />
                                {formatDate(letter.created_at)}
                              </p>
                            </div>
                          </div>
                          
                          {letter.is_mine && (
                            <button
                              onClick={() => handleDelete(letter.id)}
                              className="p-1 text-lws-taupe hover:text-red-600 transition-colors"
                              title="Delete"
                            >
                              <Trash2 size={16} />
                            </button>
                          )}
                        </div>
                        
                        {letter.note && (
                          <p className="mt-3 text-sm text-lws-taupe italic pl-7">"{letter.note}"</p>
                        )}
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </Section>
  );
}
